import Service from "../../services/Service";
import Logger from "../../utils/Logger";
import EQError from "../../utils/EQError";
import HuaweiGatewayModule from "./modules/HuaweiGatewayModule";
import * as KpiMapper from "./modules/kpi/mapper/KpiMapper";
import * as RawKpiConstants from "./modules/kpi/enums/RawKpiConstants";

const DEFAULT_GATEWAY_EXTENSIONS = ["1", "254"];
const PING_TIMEOUT = 5000; // In ms
const MODULES = [HuaweiGatewayModule];
const COLLECT_VALIDITY_TIME = 120000; // In ms
const MAX_LOGIN_ATTEMPT = 3;
const NB_LOGIN_ATTEMPT_TIMER = 2; // In seconds

export const AUTH_FAILED_INVALID_USERNAME_PASSWORD = 108006;
export const AUTH_FAILED_MAX_ATTEMPT_REACHED = 108007;

export default class GatewayService extends Service {

    /**
     * Constructor
     */
    constructor() {
        super();
        Logger.log('Ip lookup');
        this.ip = null;
        this.username = null;
        this.password = null;
        this.module = null;
        this.collectTimer = null;
        this.isCollecting = false;
        this.nbLoginAttempt = 0;
        this.cacheInfos = null;
    }

    /**
     * Init
     *
     * @param callback
     * @param ip
     * @param username
     * @param password
     */
    init(callback = null, ip = null, username = null, password = null) {
        this.ip = ip;
        this.username = username;
        this.password = password;
        this.module = null; // Reset module

        // Ip is forced and set
        const self = this;
        self.nbLoginAttempt++;
        if (this.ip) {
            this.pingIps({self: this, ips:[this.ip]}).then(this.authenticate).then((module) => {
                self.module = module;
                if (callback) callback(self.module.brand, self.module.model, null);
            }).catch((e) => {
                Logger.log(e);
                if (self.nbLoginAttempt < MAX_LOGIN_ATTEMPT && e.code != AUTH_FAILED_INVALID_USERNAME_PASSWORD && e.code != AUTH_FAILED_MAX_ATTEMPT_REACHED) {
                    setTimeout(() => {
                        self.init(callback, ip, username, password);
                    }, NB_LOGIN_ATTEMPT_TIMER * 1000);
                } else {
                    if (callback) callback(null, null, e);
                }
            });
        } else {
            this.getIps({self:this}).then(this.lookForDefaultIps).then(this.pingIps).then(this.authenticate).then((module) => {
                self.module = module;
                if (callback) callback(self.module.brand, self.module.model, null);
            }).catch((e) => {
                Logger.log(e);
                if (self.nbLoginAttempt < MAX_LOGIN_ATTEMPT && e.code != AUTH_FAILED_INVALID_USERNAME_PASSWORD && e.code != AUTH_FAILED_MAX_ATTEMPT_REACHED) {
                    setTimeout(() => {
                        self.init(callback, ip, username, password);
                    }, NB_LOGIN_ATTEMPT_TIMER * 1000);
                } else {
                    if (callback) callback(null, null, e);
                }
            });
        }

    }

    /**
     * Get a list of local ips through RTC (for all interfaces)
     *
     * @returns {Promise} A promise with the current class instance (self) and an array of ip addresses (ips)
     */
    getIps() {
        return new Promise((resolve) => {
            const ips = [];
            window.RTCPeerConnection = window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection || false;

            if (window.RTCPeerConnection)
            {
                const pc = new RTCPeerConnection({iceServers:[]}), noop = () => {};
                pc.createDataChannel('');
                pc.createOffer(pc.setLocalDescription.bind(pc), noop);

                pc.onicecandidate = (event) => {
                    if (event && event.candidate && event.candidate.candidate)
                    {
                        const s = event.candidate.candidate.split('\n');
                        ips.push(s[0].split(' ')[4]);
                    } else if (event && !event.candidate) {
                        resolve({self:this, ips:ips});
                    }
                }
            }
        });
    }

    /**
     * Generate an ip with default extensions specified in constants above
     * Can throw an error
     *
     * @param ip
     * @param extension
     * @returns {*}
     */
    static generateIpWithExtension(ip, extension) {
        const splitIp = ip.split(".");
        let result = ip;
        if (splitIp.length === 4) {
            splitIp[3] = extension;
            result = splitIp.join(".");
        } else {
            throw new EQError("Invalid IP", 8965);
        }

        return result;
    }

    /**
     * Generate a list of ips based on local ip interfaces detected and default extensions.
     * For example, if current local ip is 192.168.0.3 and default extensions are ["1", "254"], this method will return ["192.168.0.1", "192.168.0.254"]
     *
     * @returns {Promise} A promise with the current class instance (self) and an array of ip addresses (ips)
     */
    lookForDefaultIps(result) {
        return new Promise((resolve) => {
            const ips = result.ips;
            const self = result.self;
            const defaultGatewayExtensions = result.defaultGatewayExtensions ? result.defaultGatewayExtensions :  DEFAULT_GATEWAY_EXTENSIONS;
            const potentialDefaultGatewayIpList = [];
            if (ips.length > 0) {
                ips.forEach((ip) => {
                    defaultGatewayExtensions.forEach((defaultGatewaysExtension) => {
                        try {
                            const potentialDefaultGatewayIp = GatewayService.generateIpWithExtension(ip, defaultGatewaysExtension);
                            if (potentialDefaultGatewayIpList.indexOf(potentialDefaultGatewayIp) === -1) {
                                potentialDefaultGatewayIpList.push(potentialDefaultGatewayIp);
                            }
                        } catch (e) {
                            Logger.log("Error : " + e.message + " for gateway " + ip);
                        }
                    });
                });
            }

            resolve({self: self, ips:potentialDefaultGatewayIpList});
        });
    }

    /**
     * Ping a module to check if it is responding
     *
     * @param module
     * @returns {Promise} A promise with the module, the error, the headers and the delta (time to respond)
     */
    pingIpAsPromise(module) {
        return new Promise((resolve) => {
            const oReq = new XMLHttpRequest();
            const startTimestamp = Date.now();
            let success = false;
            oReq.open("get", module.getPingUrl(), true);

            oReq.onreadystatechange = function() {
                if (this.readyState === XMLHttpRequest.DONE) {
                    if (this.status >= 200 && this.status < 300) {
                        resolve({module: module, error:null, delta:(Date.now() - startTimestamp), headers:this.getAllResponseHeaders()});
                    } else {
                        // Comment : Due to Promise.race, we should not call resolve (or reject) to try others IP addresses
                        setTimeout(() => {
                            resolve({module: module, error:new EQError("HTTP status : " + oReq.status, 8967), delta:(Date.now() - startTimestamp), headers:null});
                        }, (PING_TIMEOUT - (Date.now() - startTimestamp)));
                    }
                }
            };
            oReq.addEventListener("abort", () => {
                resolve({module: module, error:new EQError("Timeout", 8968), delta:(Date.now() - startTimestamp), headers:null});
            }, false);
            setTimeout(() => {
                if (!success) {
                    oReq.abort();
                }
            }, PING_TIMEOUT);

            oReq.send();
        });
    }

    /**
     * Ping a list of ips and detect what module is behind.
     *
     * @param result An object with two parameters : self (current instance) and ips (a list of ip addresses)
     * @returns {Promise}
     */
    pingIps(result) {
        return new Promise((resolve, reject) => {
            const ipsToPingPromises = [];
            const self = result.self;
            result.ips.forEach((ip) => {
                MODULES.forEach((Module) => {
                    ipsToPingPromises.push(self.pingIpAsPromise(new Module(ip, self.username, self.password)));
                });
            });

            Promise.race(ipsToPingPromises).then((pingResult) => {
                Logger.log("Detected module " + pingResult.module.brand + " " + pingResult.module.model + " on IP " + pingResult.module.ip + ". Time to detect : " + pingResult.delta + "ms");
                resolve(pingResult.module);
            }).catch((e) => {
                reject(new EQError("Module not detected : " + e.message, 8969));
            });
        });
    }

    /**
     * Call the authentication of the module
     * @param module
     * @returns {*|Promise}
     */
    authenticate(module) {
        return module.authenticate();
    }

    /**
     * Retrieve gateway informations
     *
     * @returns {Promise}
     */
    getInfos() {
        const self = this;
        return new Promise((resolve, reject) => {
            if (self.module) {
                self.module.getApiInfo().then((infos) => {
                    if (infos[KpiMapper.RAW]) {
                        infos[KpiMapper.RAW][RawKpiConstants.API_TIMESTAMP] = Date.now();
                    }

                    this.cacheInfos = infos;
                    resolve(infos);
                }).catch((e) => {
                    reject(e);
                });
            } else {
                reject(new EQError("No module set !", 21298));
            }
        });
    }

    /**
     * Collect data
     *
     * @param cb
     * @param minTimer
     * @param lastTimestamp
     * @param self
     */
    collect(cb, minTimer, lastTimestamp, self = null) {
        if (!self) {
            self = this;
        }

        if (self.collectTimer && self.isCollecting) {
            self.getInfos().then((infos) => {
                const now = Date.now();
                const nextTick = Math.max(minTimer, (now - lastTimestamp));
                self.collectTimer = setTimeout(() => {
                    self.collect(cb, minTimer, now + nextTick, self);
                }, nextTick);
                if (nextTick < COLLECT_VALIDITY_TIME) {
                    cb(null, infos[KpiMapper.RAW]);
                } else {
                    cb(new EQError("Gateway infos too old : " + nextTick, 872586));
                }
            }).catch((e) => {
                const now = Date.now();
                const nextTick = Math.max(minTimer, (now - lastTimestamp));
                self.collectTimer = setTimeout(() => {
                    self.collect(cb, minTimer, now + nextTick, self);
                }, nextTick);
                cb(e);
            });
        }
    }

    /**
     * Start collect until stop is called
     *
     * @param cb
     * @param minTimer
     */
    startCollect(cb, minTimer = 500) {
        const self = this;
        if (cb && this.module) {
            if (!this.collectTimer && !this.isCollecting) {
                this.isCollecting = true;
                this.collectTimer = setTimeout(() => {
                    self.collect(cb, minTimer, Date.now(), self);
                }, 0);
            } else {
                cb(new EQError("Gateway infos already collecting", 872538));
            }
        } else {
            cb(new EQError("No callback or module detected", 872537));
        }
    }

    /**
     * Stop a running collect
     */
    stopCollect() {
        if (this.collectTimer) {
            clearTimeout(this.collectTimer);
            this.collectTimer = null;
        }
        this.isCollecting = false;
    }

    /**
     * Get cache informations
     */
    getCacheInfos() {
        const now = Date.now();
        if (this.cacheInfos && (now - this.cacheInfos[KpiMapper.RAW][RawKpiConstants.API_TIMESTAMP] < COLLECT_VALIDITY_TIME)) {
            return this.cacheInfos;
        } else {
            return null;
        }
    }
}


// WEBPACK FOOTER //
// ./js/core/gateway/service/GatewayService.js